import { useMemo, useState } from "react";
import { Image as ImageIcon, MapPin, User } from "lucide-react";
import { ScrollArea } from "@/client/components/ui/scroll-area";
import { useAllImages, type ImageWithCodexInfo } from "@/client/hooks/use-all-images";
import type { ImageCategory } from "@/db/schema";
import { ImagePreviewDialog } from "./image-preview-dialog";

/** The kinds shown after portraits and backgrounds, by plain name and shape */
const OTHER_CATEGORIES: { category: ImageCategory; label: string; columns: string; aspect: string }[] = [
  { category: "sprite", label: "Sprites", columns: "grid-cols-3", aspect: "aspect-[3/4]" },
  { category: "expression", label: "Expressions", columns: "grid-cols-3", aspect: "aspect-[3/4]" },
  { category: "outfit", label: "Outfits", columns: "grid-cols-3", aspect: "aspect-[3/4]" },
  { category: "sheet", label: "Reference sheets", columns: "grid-cols-2", aspect: "aspect-video" },
  { category: "cg", label: "CGs, the full-screen pictures", columns: "grid-cols-2", aspect: "aspect-video" },
  { category: "reference", label: "Pictures to draw from", columns: "grid-cols-3", aspect: "aspect-square" },
];

interface ImagesPanelProps {
  storyId: string;
  searchQuery?: string;
}

export function ImagesPanel({ storyId, searchQuery = "" }: ImagesPanelProps) {
  const { imagesByCategory, isLoading, images, refetch } = useAllImages(storyId);
  const [selectedImage, setSelectedImage] = useState<ImageWithCodexInfo | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  const handleImageClick = (image: ImageWithCodexInfo) => {
    setSelectedImage(image);
    setShowPreview(true);
  };

  const handleImageDeleted = () => {
    refetch();
  };

  // Filter by search query (searches name and codex name)
  const filteredImages = useMemo(() => {
    if (!searchQuery.trim()) return images;

    const query = searchQuery.toLowerCase();
    return images.filter(
      (img: ImageWithCodexInfo) =>
        img.name.toLowerCase().includes(query) ||
        (img.codexName && img.codexName.toLowerCase().includes(query))
    );
  }, [images, searchQuery]);

  // Group filtered images by category, every kind of image there is
  const filteredByCategory = useMemo(() => {
    const groups: Record<ImageCategory, ImageWithCodexInfo[]> = {
      portrait: [],
      background: [],
      sprite: [],
      sheet: [],
      expression: [],
      outfit: [],
      cg: [],
      reference: [],
    };
    filteredImages.forEach((img: ImageWithCodexInfo) => {
      groups[img.category]?.push(img);
    });
    return groups;
  }, [filteredImages]);

  // Group backgrounds by location (codexId)
  const backgroundsByLocation = useMemo(() => {
    const groups = new Map<string, ImageWithCodexInfo[]>();
    filteredByCategory.background.forEach((img: ImageWithCodexInfo) => {
      const key = img.codexId || "unlinked";
      const existing = groups.get(key) ?? [];
      existing.push(img);
      groups.set(key, existing);
    });
    return groups;
  }, [filteredByCategory.background]);

  if (isLoading) {
    return (
      <div className="p-4 text-center text-muted-foreground text-sm">
        Loading...
      </div>
    );
  }

  if (images.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <ImageIcon className="h-10 w-10 text-muted-foreground/50 mb-3" />
        <p className="text-sm text-muted-foreground mb-1">No images yet</p>
        <p className="text-xs text-muted-foreground/70">
          Add images to codex entries
        </p>
      </div>
    );
  }

  if (filteredImages.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <ImageIcon className="h-10 w-10 text-muted-foreground/50 mb-3" />
        <p className="text-sm text-muted-foreground">No matching images</p>
      </div>
    );
  }

  return (
    <>
    <ScrollArea className="h-[300px]">
      <div className="space-y-4 pr-4">
        {/* Portraits Section */}
        {filteredByCategory.portrait.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <User className="h-3 w-3 text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Portraits
              </span>
              <span className="text-xs text-muted-foreground/60">
                ({filteredByCategory.portrait.length})
              </span>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {filteredByCategory.portrait.map((img) => (
                <button
                  type="button"
                  key={img.id}
                  className="group relative rounded-md overflow-hidden border bg-muted aspect-square cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all"
                  onClick={() => handleImageClick(img)}
                  title={`${img.codexName || img.name} - Click to view details`}
                >
                  <img
                    src={`/api/images/${img.id}?size=thumb`}
                    alt={img.codexName || img.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-1">
                    <span className="text-[9px] text-white truncate block">
                      {img.codexName || img.name}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Backgrounds Section */}
        {filteredByCategory.background.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <MapPin className="h-3 w-3 text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Backgrounds
              </span>
              <span className="text-xs text-muted-foreground/60">
                ({filteredByCategory.background.length})
              </span>
            </div>

            <div className="space-y-3">
              {Array.from(backgroundsByLocation.entries()).map(([codexId, imgs]) => (
                <div key={codexId}>
                  {/* Location name */}
                  <div className="text-[10px] text-muted-foreground mb-1 pl-1">
                    {imgs[0]?.codexName || "Unlinked"}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {imgs.map((img) => (
                      <button
                        type="button"
                        key={img.id}
                        className="group relative rounded-md overflow-hidden border bg-muted aspect-video cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all"
                        onClick={() => handleImageClick(img)}
                        title={`${img.name}${img.isDefault ? " (Default)" : ""} - Click to view details`}
                      >
                        <img
                          src={`/api/images/${img.id}?size=thumb`}
                          alt={img.name}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-1.5">
                          <div className="flex items-center gap-1">
                            <span className="text-[10px] text-white truncate">
                              {img.name}
                            </span>
                            {img.isDefault && (
                              <span className="text-[8px] px-1 py-0.5 rounded bg-primary/80 text-primary-foreground shrink-0">
                                Default
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {OTHER_CATEGORIES.map(({ category, label, columns, aspect }) => {
          const imgs = filteredByCategory[category];
          if (imgs.length === 0) return null;
          return (
            <div key={category}>
              <div className="flex items-center gap-2 mb-2">
                <ImageIcon className="h-3 w-3 text-muted-foreground" />
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {label}
                </span>
                <span className="text-xs text-muted-foreground/60">
                  ({imgs.length})
                </span>
              </div>
              <div className={`grid ${columns} gap-2`}>
                {imgs.map((img) => (
                  <button
                    type="button"
                    key={img.id}
                    className={`group relative rounded-md overflow-hidden border bg-muted ${aspect} cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all`}
                    onClick={() => handleImageClick(img)}
                    title={`${img.codexName ? `${img.codexName}: ` : ""}${img.name} - Click to view details`}
                  >
                    <img
                      src={`/api/images/${img.id}?size=thumb`}
                      alt={img.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-1">
                      <span className="text-[9px] text-white truncate block">
                        {img.name}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </ScrollArea>

    <ImagePreviewDialog
      open={showPreview}
      onOpenChange={setShowPreview}
      image={selectedImage}
      onDeleted={handleImageDeleted}
    />
    </>
  );
}
